import type { SearchResult } from '@/types/law'
import { LAW_TYPE_LABELS, LAW_TYPE_COLORS } from '@/types/law'

interface Props {
  results: SearchResult[]
  selectedId?: string
  onSelect: (result: SearchResult) => void
  isLoading?: boolean
  query?: string
}

export default function ResultList({ results, selectedId, onSelect, isLoading, query }: Props) {
  if (isLoading) {
    return (
      <div className="flex flex-col gap-2">
        {Array.from({ length: 6 }).map((_, i) => (
          <div key={i} className="animate-pulse p-4 border border-gray-100 rounded-xl bg-white">
            <div className="flex gap-2 mb-2">
              <div className="h-4 w-12 bg-gray-100 rounded-full" />
              <div className="h-4 w-24 bg-gray-100 rounded" />
            </div>
            <div className="h-5 w-3/4 bg-gray-200 rounded mb-1" />
            <div className="h-4 w-1/2 bg-gray-100 rounded" />
          </div>
        ))}
      </div>
    )
  }

  if (!results.length && query) {
    return (
      <div className="flex flex-col items-center justify-center py-16 text-center">
        <div className="text-4xl mb-3">🔎</div>
        <p className="text-gray-500 font-medium">
          <span className="text-gray-800 font-semibold">"{query}"</span>에 대한 검색 결과가 없습니다.
        </p>
        <p className="text-sm text-gray-400 mt-1">
          법령명, 판례 사건번호, 조문 내용 등으로 다시 검색해보세요.
        </p>
        <div className="mt-4 flex flex-col gap-1 text-sm text-gray-400">
          <p>💡 검색 팁:</p>
          <p>• 약칭 사용 가능 (예: 화관법 → 화학물질관리법)</p>
          <p>• 조문번호 포함 검색 (예: 근로기준법 제74조)</p>
        </div>
      </div>
    )
  }

  if (!results.length) {
    return (
      <div className="flex flex-col items-center justify-center py-16 text-center text-gray-400">
        <div className="text-4xl mb-3">⚖️</div>
        <p className="font-medium">법령을 검색해보세요</p>
        <p className="text-sm mt-1">법령명, 판례, 행정규칙을 통합 검색할 수 있습니다.</p>
      </div>
    )
  }

  return (
    <div className="flex flex-col gap-2">
      {results.map((result) => {
        const isSelected = result.id === selectedId
        const typeLabel = LAW_TYPE_LABELS[result.type]
        const typeColor = LAW_TYPE_COLORS[result.type]

        return (
          <button
            key={result.id}
            onClick={() => onSelect(result)}
            className={`
              w-full text-left p-4 border rounded-xl transition-all
              ${isSelected
                ? 'border-law-300 bg-law-50 shadow-sm'
                : 'border-gray-100 bg-white hover:border-gray-200 hover:shadow-sm'
              }
            `}
          >
            <div className="flex items-start justify-between gap-2 mb-1">
              <span className={`inline-flex text-xs font-semibold px-2 py-0.5 rounded-full ${typeColor}`}>
                {typeLabel}
              </span>
              {result.date && (
                <span className="text-xs text-gray-400 flex-shrink-0">
                  {formatDate(result.date)}
                </span>
              )}
            </div>
            <p className={`text-sm font-semibold leading-snug
              ${isSelected ? 'text-law-800' : 'text-gray-800'}`}>
              {highlightQuery(result.title, query)}
            </p>
            {result.subtitle && (
              <p className="text-xs text-gray-500 mt-0.5">{result.subtitle}</p>
            )}
          </button>
        )
      })}
    </div>
  )
}

function formatDate(date: string): string {
  if (date.length === 8) {
    return `${date.slice(0, 4)}.${date.slice(4, 6)}.${date.slice(6, 8)}`
  }
  return date
}

function highlightQuery(text: string, query?: string): React.ReactNode {
  if (!query) return text
  const idx = text.indexOf(query)
  if (idx === -1) return text
  return (
    <>
      {text.slice(0, idx)}
      <mark className="bg-yellow-100 text-yellow-800 rounded px-0.5">{text.slice(idx, idx + query.length)}</mark>
      {text.slice(idx + query.length)}
    </>
  )
}
